import { NgClass } from "@angular/common";
import {
  Component,
  ElementRef,
  signal,
  viewChild,
  inject,
  computed,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
} from "@angular/router";
import { RouterLinkActive } from "@angular/router";
import { IconComponent } from "@claas/claas-ui-components";
import { TranslatePipe } from "@ngx-translate/core";
import { filter, Subscription } from "rxjs";
import { pathParamsHelper } from "../../helpers/path-params.helper";
import { DefaultDocCat, DefaultProduct } from "../../pia-api/generated";

@Component({
  selector: "claas-product-detail-aside",
  imports: [
    IconComponent,
    NgClass,
    TranslatePipe,
    RouterLink,
    RouterLinkActive,
  ],
  templateUrl: "./product-detail-aside.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./product-detail-aside.scss",
})
export class ProductDetailAside implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private routerEventsSubscription: Subscription | undefined;
  isDocumentsExpanded = false;
  isProductsExpanded = false;
  isPicturesExpanded = false;

  activeDescendantId = signal<string | null>(null);

  data = toSignal(this.route.data);
  productDocumentCategories = computed(
    () => this.data()?.["productDocumentCategories"] as DefaultDocCat[] | null,
  );
  productDescendants = computed(
    () => this.data()?.["productDescendants"] as DefaultProduct[] | null,
  );
  asideOpenOnSmallerViewports = signal(false);

  toggleFilterButton =
    viewChild<ElementRef<HTMLButtonElement>>("toggleFilterButton");

  handleAsideButtonDisplayClick(forceValue?: boolean) {
    const toggleState =
      typeof forceValue === "boolean"
        ? forceValue
        : !this.asideOpenOnSmallerViewports();

    this.asideOpenOnSmallerViewports.set(toggleState);

    if (!toggleState) {
      this.toggleFilterButton()?.nativeElement?.focus();
    }
  }

  ngOnInit(): void {
    this.setActiveDescendant(this.router.url);
    this.updateAccordionState(this.router.url);

    this.routerEventsSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.setActiveDescendant(event.url);
        this.updateAccordionState(event.url);
      });
  }

  private setActiveDescendant(url: string): void {
    const urlSegments = url.split("/");

    if (
      urlSegments.includes("arguments") ||
      urlSegments.includes("tech-data")
    ) {
      if (this.route.firstChild) {
        const { productDetailID } = pathParamsHelper(
          this.route.firstChild.snapshot,
        );
        this.activeDescendantId.set(productDetailID);
      }
    } else {
      this.activeDescendantId.set(null);
    }
  }

  private updateAccordionState(url: string): void {
    this.isDocumentsExpanded = false;
    this.isProductsExpanded = false;
    this.isPicturesExpanded = false;

    if (url.includes("/documents/")) {
      this.isDocumentsExpanded = true;
    } else if (url.includes("/arguments/") || url.includes("/tech-data/")) {
      this.isProductsExpanded = true;
    }
  }

  ngOnDestroy(): void {
    this.routerEventsSubscription?.unsubscribe();
  }
}
